import React from 'react';
import { useNavigate } from 'react-router-dom';
import { privacyService, PrivacyNotice } from '../services/privacyService';
import DismissibleError from './DismissibleError';

interface PrivacyAgreementProps {
  authenticatedHome: string;
  onAccepted: () => void;
  onLogout: () => Promise<void>;
  requireAcceptance?: boolean;
}

const PrivacyAgreement: React.FC<PrivacyAgreementProps> = ({
  authenticatedHome,
  onAccepted,
  onLogout,
  requireAcceptance = false,
}) => {
  const navigate = useNavigate();
  const [notice, setNotice] = React.useState<PrivacyNotice | null>(null);
  const [accepted, setAccepted] = React.useState(false);
  const [hasActiveConsent, setHasActiveConsent] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let mounted = true;
    privacyService.getStatus()
      .then((response) => {
        if (!mounted) return;

        setHasActiveConsent(response.has_active_consent);
        setNotice(response.notice);

        if (response.has_active_consent) {
          onAccepted();

          if (requireAcceptance) {
            navigate(authenticatedHome, { replace: true });
          }
        }
      })
      .catch(() => {
        if (mounted) setError('CyberShield could not load the Data Privacy Agreement.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, [authenticatedHome, navigate, onAccepted, requireAcceptance]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!accepted) {
      setError('You must acknowledge the Data Privacy Agreement before continuing.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const response = await privacyService.accept();
      if (!response.success || !response.has_active_consent) {
        setError(response.error || 'CyberShield could not record your acknowledgement.');
        return;
      }
      onAccepted();
      navigate(authenticatedHome, { replace: true });
    } catch {
      setError('CyberShield could not record your acknowledgement. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="privacy-page">
      <section className="privacy-panel" aria-labelledby="privacy-title">
        <img className="login-logo" src="/cybershield-logo.png" alt="CyberShield" />
        <div className="privacy-heading">
          <p className="overline">{requireAcceptance ? 'Required acknowledgement' : 'Read-only notice'}</p>
          <h1 id="privacy-title">CyberShield Data Privacy Agreement</h1>
          {notice && <p>Version {notice.version} | Effective {notice.effective_date}</p>}
        </div>

        {error && <DismissibleError message={error} onDismiss={() => setError('')} />}

        {loading ? (
          <div className="loading-state"><span className="loading-mark" />Loading agreement...</div>
        ) : (
          <>
            <div className="privacy-notice-box">
              <h2>{notice?.title || 'Data Privacy Agreement'}</h2>
              {(notice?.content || 'No active Data Privacy Agreement is currently configured.')
                .split('\n')
                .map((paragraph, index) => paragraph.trim() ? <p key={index}>{paragraph}</p> : null)}
            </div>

            {hasActiveConsent && !requireAcceptance ? (
              <div className="privacy-actions">
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => navigate(authenticatedHome)}
                >
                  Back to Dashboard
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <label className="privacy-acknowledgement">
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={(event) => setAccepted(event.target.checked)}
                    required
                  />
                  <span>
                    I have read and understood the CyberShield Data Privacy Agreement and acknowledge the
                    processing of my personal and system activity data for authorized firearm accountability,
                    security, audit, and compliance purposes.
                  </span>
                </label>

                <div className="privacy-actions">
                  <button className="btn btn-primary" type="submit" disabled={submitting}>
                    {submitting ? 'Recording...' : 'Agree and Continue'}
                  </button>
                  <button className="btn btn-secondary" type="button" onClick={onLogout} disabled={submitting}>
                    Logout
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </section>
    </main>
  );
};

export default PrivacyAgreement;
